/**
 * Settings management page component for admins.
 */
import React, { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { useSettings } from '../contexts/SettingsContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { updateSettings, SettingsUpdate } from '../api/settings';
import { getAvailableThemes } from '../themes/manager';
import './SettingsPage.css';

export const SettingsPage: React.FC = () => {
  const { settings, refreshSettings } = useSettings();
  const { isConnected, on, off } = useWebSocket();
  const [appName, setAppName] = useState('');
  const [colorScheme, setColorScheme] = useState('professional');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setAppName(settings.app_name);
      setColorScheme(settings.color_scheme);
    }
  }, [settings]);

  // Register WebSocket event handlers for settings updates
  useEffect(() => {
    const handleSettingsUpdate = async (message: { event: string; data?: any }) => {
      const { settings_data } = message.data || {};
      
      if (settings_data) {
        // Update local state from WebSocket message
        setAppName(settings_data.app_name || '');
        setColorScheme(settings_data.color_scheme || 'professional');
        // Refresh settings context
        await refreshSettings();
      }
    };

    const unsubscribe = on('settings_update', handleSettingsUpdate);

    return () => {
      unsubscribe();
    };
  }, [on, off, refreshSettings]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const updates: SettingsUpdate = {
        app_name: appName.trim(),
        color_scheme: colorScheme,
      };

      await updateSettings(updates);
      setSuccess('Settings updated successfully!');
      await refreshSettings();
    } catch (err: any) {
      let errorMessage = 'Failed to update settings. Please try again.';
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setAppName(settings.app_name);
      setColorScheme(settings.color_scheme);
      setError(null);
      setSuccess(null);
    }
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      setAppName('dasher');
      setColorScheme('professional');
      setError(null);
      setSuccess(null);
    }
  };

  if (!settings) {
    return (
      <AdminLayout>
        <div className="settings-page">
          <div className="settings-container">
            <div className="loading-message">Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="settings-page">
        <div className="settings-container">
          <div className="admin-breadcrumb">
            <Link to="/admin">Admin</Link>
            <span className="admin-breadcrumb-separator">/</span>
            <span className="admin-breadcrumb-current">Settings</span>
          </div>
          <h1>Settings</h1>

          <form onSubmit={handleSubmit} className="settings-form" role="form">
            {error && (
              <div className="error-message" role="alert">
                <strong>Error:</strong> {error}
              </div>
            )}

            {success && (
              <div className="success-message" role="alert">
                {success}
              </div>
            )}

            <section className="settings-section">
              <div className="form-group">
                <label htmlFor="app-name">Application Name</label>
                <input
                  id="app-name"
                  type="text"
                  value={appName}
                  onChange={(e) => {
                    setAppName(e.target.value);
                    setError(null);
                  }}
                  required
                  disabled={isLoading}
                  placeholder="Enter application name"
                  minLength={1}
                  maxLength={100}
                />
                <small className="form-hint">
                  This name appears in the navigation bar and page titles
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="color-scheme">Color Scheme</label>
                <select
                  id="color-scheme"
                  value={colorScheme}
                  onChange={(e) => {
                    setColorScheme(e.target.value);
                    setError(null);
                  }}
                  disabled={isLoading}
                  className="select-input"
                >
                  {(getAvailableThemes() || []).map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      {theme.name}
                    </option>
                  ))}
                </select>
                <small className="form-hint">
                  Select the color scheme/theme for the application
                </small>
              </div>
            </section>

            <div className="form-actions">
              <button
                type="button"
                className="btn-reset"
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Changes
              </button>
              <button
                type="button"
                className="btn-reset-defaults"
                onClick={handleResetToDefaults}
                disabled={isLoading}
              >
                Reset to Defaults
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

